/**
 * Dialog de configuração do node "Alterar Fase da Tarefa" (task_change_phase)
 *
 * RELEASE: 1.0.0 - 2026-05-31 - Versão inicial
 *
 * Permite escolher origem da tarefa (gatilho ou variável) e a fase de destino.
 * A categoria é exigida apenas para filtrar as fases visíveis — a validação
 * "fase pertence à mesma categoria da tarefa" é feita server-side pela RPC
 * `move_task_phase` em runtime.
 */

import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Separator } from '@/components/ui/separator';
import { ArrowRightLeft, MapPin, ListTodo, HelpCircle } from 'lucide-react';
import { FlowNode, TaskChangePhaseConfig } from '@/types/flows';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { showToast } from '@/lib/toast';
import { NodeHelpDialog } from './NodeHelpDialog';
import { NodeIdField, NodeIdFieldRef } from '../NodeIdField';
import { VariablePickerCombobox } from '../VariablePickerCombobox';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  node: FlowNode;
  onSave: (nodeId: string, config: TaskChangePhaseConfig) => void;
}

export function ConfigureTaskChangePhaseDialog({ open, onOpenChange, node, onSave }: Props) {
  const { currentTenant } = useAuth();
  const tenantId = currentTenant?.id || '';
  const nicknameRef = useRef<NodeIdFieldRef>(null);

  const existing = node.data.config as TaskChangePhaseConfig | undefined;

  // Estado: origem da tarefa
  const [taskSource, setTaskSource] = useState<TaskChangePhaseConfig['task_source']>(
    existing?.task_source || 'from_trigger',
  );
  const [taskVariable, setTaskVariable] = useState(existing?.task_variable || '');
  const [taskVariableId, setTaskVariableId] = useState(existing?.task_variable_id || '');

  // Estado: fase destino
  const [categoryId, setCategoryId] = useState(existing?.category_id || '');
  const [categoryName, setCategoryName] = useState(existing?.category_name || '');
  const [categoryColor, setCategoryColor] = useState(existing?.category_color || '');
  const [phaseId, setPhaseId] = useState(existing?.phase_id || '');
  const [phaseName, setPhaseName] = useState(existing?.phase_name || '');
  const [phaseColor, setPhaseColor] = useState(existing?.phase_color || '');

  const [showHelp, setShowHelp] = useState(false);

  // Reset ao reabrir o dialog
  useEffect(() => {
    if (!open) return;
    const cfg = node.data.config as TaskChangePhaseConfig | undefined;
    setTaskSource(cfg?.task_source || 'from_trigger');
    setTaskVariable(cfg?.task_variable || '');
    setTaskVariableId(cfg?.task_variable_id || '');
    setCategoryId(cfg?.category_id || '');
    setCategoryName(cfg?.category_name || '');
    setCategoryColor(cfg?.category_color || '');
    setPhaseId(cfg?.phase_id || '');
    setPhaseName(cfg?.phase_name || '');
    setPhaseColor(cfg?.phase_color || '');
  }, [open, node.id]);

  // Carrega categorias do tenant
  const { data: categories, isLoading: loadingCategories } = useQuery({
    queryKey: ['flow-task-categories', tenantId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('task_categories')
        .select('id, name, color')
        .eq('tenant_id', tenantId)
        .eq('is_active', true)
        .order('sort_order', { ascending: true });
      if (error) throw error;
      return data || [];
    },
    enabled: !!tenantId && open,
  });

  // Carrega fases da categoria escolhida
  const { data: phases, isLoading: loadingPhases } = useQuery({
    queryKey: ['flow-task-phases', tenantId, categoryId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('task_phases')
        .select('id, name, color')
        .eq('tenant_id', tenantId)
        .eq('category_id', categoryId)
        .eq('is_active', true)
        .order('sort_order', { ascending: true });
      if (error) throw error;
      return data || [];
    },
    enabled: !!tenantId && !!categoryId && open,
  });

  const handleCategoryChange = (id: string) => {
    const c = categories?.find((x) => x.id === id);
    setCategoryId(id);
    setCategoryName(c?.name || '');
    setCategoryColor(c?.color || '');
    setPhaseId('');
    setPhaseName('');
    setPhaseColor('');
  };

  const handlePhaseChange = (id: string) => {
    const p = phases?.find((x) => x.id === id);
    setPhaseId(id);
    setPhaseName(p?.name || '');
    setPhaseColor(p?.color || '');
  };

  const isFormValid = (): boolean => {
    if (taskSource === 'from_variable' && !taskVariable.trim()) return false;
    if (!categoryId || !phaseId) return false;
    return true;
  };

  const handleSave = () => {
    if (!isFormValid()) {
      showToast.error('Preencha todos os campos obrigatórios');
      return;
    }
    const config: TaskChangePhaseConfig = {
      nickname: nicknameRef.current?.getNickname() || undefined,
      task_source: taskSource,
      task_variable: taskSource === 'from_variable' ? taskVariable.trim() : undefined,
      task_variable_id: taskSource === 'from_variable' ? taskVariableId || undefined : undefined,
      category_id: categoryId,
      category_name: categoryName || undefined,
      category_color: categoryColor || undefined,
      phase_id: phaseId,
      phase_name: phaseName || undefined,
      phase_color: phaseColor || undefined,
    };
    onSave(node.id, config);
    onOpenChange(false);
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-2xl h-[85vh] max-h-[85vh] flex flex-col overflow-hidden min-h-0">
          <DialogHeader className="shrink-0">
            <div className="flex items-center justify-between">
              <DialogTitle className="flex items-center gap-2">
                <ArrowRightLeft className="h-5 w-5 text-amber-600" />
                Configurar Alterar Fase da Tarefa
              </DialogTitle>
              <Button variant="ghost" size="icon" onClick={() => setShowHelp(true)}>
                <HelpCircle className="h-5 w-5" />
              </Button>
            </div>
            <DialogDescription>
              Move uma tarefa existente para outra fase da mesma categoria
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 min-h-0 overflow-y-auto pr-4">
            <div className="space-y-4 py-4">
              <NodeIdField ref={nicknameRef} nodeId={node?.id} defaultNickname={node?.data.config?.nickname} />

              {/* ===================== TAREFA DE ORIGEM ===================== */}
              <div className="space-y-3">
                <div className="flex items-center gap-2 text-sm font-semibold text-amber-600 dark:text-amber-400">
                  <ListTodo className="h-4 w-4" />
                  Tarefa de origem
                </div>
                <RadioGroup value={taskSource} onValueChange={(v) => setTaskSource(v as any)}>
                  <div className="flex items-start space-x-2">
                    <RadioGroupItem value="from_trigger" id="ts-trigger" className="mt-1" />
                    <div className="space-y-0.5">
                      <Label htmlFor="ts-trigger" className="font-normal cursor-pointer">
                        Tarefa do gatilho
                      </Label>
                      <p className="text-xs text-muted-foreground">
                        Usa o <code>task_id</code> propagado por um gatilho de Tarefa
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start space-x-2">
                    <RadioGroupItem value="from_variable" id="ts-var" className="mt-1" />
                    <div className="space-y-0.5">
                      <Label htmlFor="ts-var" className="font-normal cursor-pointer">
                        De uma variável
                      </Label>
                      <p className="text-xs text-muted-foreground">
                        Usa um <code>task_id</code> armazenado em variável (ex.: saída de "Criar Tarefa")
                      </p>
                    </div>
                  </div>
                </RadioGroup>

                {taskSource === 'from_variable' && (
                  <VariablePickerCombobox
                    tenantId={tenantId}
                    value={taskVariableId || taskVariable}
                    onChange={(v) => {
                      if (v) {
                        setTaskVariableId(v.id);
                        setTaskVariable(v.name);
                      } else {
                        setTaskVariableId('');
                        setTaskVariable('');
                      }
                    }}
                    allowCreate={false}
                    allowClear={false}
                    placeholder="Variável com ID da tarefa"
                  />
                )}
              </div>

              <Separator />

              {/* ===================== FASE DESTINO ===================== */}
              <div className="space-y-3">
                <div className="flex items-center gap-2 text-sm font-semibold text-amber-600 dark:text-amber-400">
                  <MapPin className="h-4 w-4" />
                  Fase de destino
                </div>

                <div className="space-y-2">
                  <Label>Categoria *</Label>
                  <Select value={categoryId} onValueChange={handleCategoryChange} disabled={loadingCategories}>
                    <SelectTrigger>
                      <SelectValue placeholder={loadingCategories ? 'Carregando...' : 'Selecione a categoria'} />
                    </SelectTrigger>
                    <SelectContent>
                      {categories?.map((c) => (
                        <SelectItem key={c.id} value={c.id}>
                          <div className="flex items-center gap-2">
                            {c.color && (
                              <span className="w-3 h-3 rounded-full" style={{ backgroundColor: c.color }} />
                            )}
                            <span>{c.name}</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    A tarefa em runtime precisa pertencer a esta categoria — a validação é feita pelo banco.
                  </p>
                </div>

                <div className="space-y-2">
                  <Label>Fase de destino *</Label>
                  <Select value={phaseId} onValueChange={handlePhaseChange} disabled={!categoryId || loadingPhases}>
                    <SelectTrigger>
                      <SelectValue
                        placeholder={
                          !categoryId
                            ? 'Selecione a categoria primeiro'
                            : loadingPhases
                            ? 'Carregando...'
                            : 'Selecione a fase'
                        }
                      />
                    </SelectTrigger>
                    <SelectContent>
                      {phases?.map((p) => (
                        <SelectItem key={p.id} value={p.id}>
                          <div className="flex items-center gap-2">
                            {p.color && (
                              <span className="w-3 h-3 rounded-full" style={{ backgroundColor: p.color }} />
                            )}
                            <span>{p.name}</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          </div>

          <DialogFooter className="shrink-0 border-t pt-4">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSave} disabled={!isFormValid()}>
              Salvar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <NodeHelpDialog open={showHelp} onOpenChange={setShowHelp} nodeType="task_change_phase" />
    </>
  );
}
